import * as React from 'react';
import { PolymorphicRef } from '../../../utils/PolymorphicTypes';
import { LinkTextProps, LinkTextComponent } from './LinkTextTypes';
import { GenericThemeColorsAvailable } from '../../foundation/colors/GenericThemeColorsTypes';
import { variantStyleObject } from './LinkStyleObjects';
import { useLinkStyle } from './hooks/useLinkStyle';
import { useLinkTheme } from '../../../themes';
import { Icon } from '../../index';
import { LinkBase } from './LinkBase';

export const LinkText: LinkTextComponent = React.forwardRef(
    <C extends React.ElementType = 'a'>
    ({
    icon,
    iconPosition='left',
    iconProps,
    ...linkProps
}:LinkTextProps<C>, 
ref?:PolymorphicRef<C>) => {

    const { linkTheme } = useLinkTheme();
    const _colorStyle = linkProps?.colorStyle || 'default';
    const _disabled = typeof linkProps?.disabled === 'boolean' ? linkProps?.disabled : false;
    const _decorator = linkProps?.decorator || 'always';
    let {actionStateProps, isHovered, isActive, showLinkStyle} = useLinkStyle({
        decorator: _decorator,
        disabled: _disabled,
        onKeyDown: linkProps?.onKeyDown,
        onKeyUp: linkProps?.onKeyUp,
        onMouseDown: linkProps?.onMouseDown,
        onMouseUp: linkProps?.onMouseUp,
        onTouchStart: linkProps?.onTouchStart,
        onTouchEnd: linkProps?.onTouchEnd,
    })

    const showActiveStyle = linkProps?.noRoute ? false : (isHovered || isActive);
    const showHovered = !_disabled && !linkProps?.noRoute && isHovered;
    const _bold = typeof linkProps?.typographyProps?.bold === 'boolean' ? linkProps?.typographyProps?.bold : showLinkStyle;
    const _display = linkProps?.display;
    const _underline = typeof linkProps?.typographyProps?.underline === 'boolean' ? linkProps?.typographyProps?.underline : showActiveStyle;
    const _overrideHoverColor = typeof linkProps?.overrideHoverColor === 'boolean' ? linkProps?.overrideHoverColor : false;
    const showActiveColor = !_disabled && !_overrideHoverColor && showActiveStyle;
    const _activeColor:GenericThemeColorsAvailable = variantStyleObject[_colorStyle]['active']?.color;
    const _inactiveColor:GenericThemeColorsAvailable = linkProps?.color || (showLinkStyle ? variantStyleObject[_colorStyle]['inactive']?.color : undefined);

    const renderLinkIcon = () => {
        return (
            <div className={linkTheme['bsw-link-text__icon']}>
                <Icon 
                    ariaLabel={iconProps?.ariaLabel}
                    color={showActiveColor ? _activeColor : (iconProps?.color || _inactiveColor)}
                    disabled={_disabled} 
                    name={icon} 
                    set="system"
                    size={iconProps?.size || "medium"}
                />
            </div>
        );
    };

    const linkContent = <>
        {iconPosition === 'left' && icon && renderLinkIcon()}
        {linkProps?.children}
        {iconPosition === 'right' && icon && renderLinkIcon()}
    </>;

    return (
        <LinkBase
            {...linkProps}
            {...actionStateProps}
            color={showActiveColor ? _activeColor : _inactiveColor}
            className={showHovered ? 'bsw-link-text--hover' : ''}
            colorStyle={_colorStyle}
            decorator={_decorator}
            display={_display}
            overrideHoverColor={_overrideHoverColor}
            ref={ref}
            typographyProps={{
                ...linkProps?.typographyProps,
                bold: _bold,
                color: showActiveColor ? _activeColor : (linkProps?.typographyProps?.color || _inactiveColor),
                underline: _underline
            }}
            variant="text"
        >
            {icon
                ? <div className={linkTheme['bsw-link-text__icon-container']}>
                    <div className={linkTheme['bsw-link-text__icon-content']}>
                        {linkContent}
                    </div>
                </div>
                : linkContent
            }
        </LinkBase>
    );
});